import { useCallback, useEffect, useRef, useState } from 'react'
import type { FullPokemonCard } from '@/types/pokemon'
import { addCard } from '../services/inventoryService'
import ScanCardAlternatives from './ScanCardAlternatives'
import type { StandardCondition } from '@/types/inventory'
import { getApiUrl } from '@/lib/utils'
import { AddToInventoryModal } from './inventory/AddToInventoryModal'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import cvModule from '@techstark/opencv-js'

const CARD_ASPECT_RATIO = 2.5 / 3.5
const ASPECT_TOLERANCE = 0.18
const MIN_CARD_AREA_RATIO = 0.015
const MAX_CARD_AREA_RATIO = 0.5
const MIN_AREA_PX = 4000
/** Above this, JPEG encode + base64 + upload dominate on mobile; card ID does not need full sensor resolution. */
const MAX_CAPTURE_LONG_EDGE = 1280

function shouldPreferVideoSnapshot(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (window.matchMedia('(pointer: coarse)').matches) return true
  } catch {
    /* matchMedia unavailable */
  }
  return navigator.maxTouchPoints > 0
}

function prepareJpegExportCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const w = canvas.width
  const h = canvas.height
  const longEdge = Math.max(w, h)
  if (longEdge <= MAX_CAPTURE_LONG_EDGE) return canvas
  const scale = MAX_CAPTURE_LONG_EDGE / longEdge
  const nw = Math.max(1, Math.round(w * scale))
  const nh = Math.max(1, Math.round(h * scale))
  const scaled = document.createElement('canvas')
  scaled.width = nw
  scaled.height = nh
  const ctx = scaled.getContext('2d')
  if (!ctx) return canvas
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(canvas, 0, 0, nw, nh)
  return scaled
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  const exportCanvas = prepareJpegExportCanvas(canvas)
  return new Promise((resolve, reject) => {
    exportCanvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('JPEG encode failed'))),
      'image/jpeg',
      quality,
    )
  })
}

type DetectedCard = {
  corners: { x: number; y: number }[]
  confidence: number
}

type ScanCardLiveProps = {
  onCameraActiveChange?: (active: boolean) => void
}

export function ScanCardLive({ onCameraActiveChange }: ScanCardLiveProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const overlayRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const animationRef = useRef<number | null>(null)
  const cvRef = useRef<unknown>(null)
  const lastCardsRef = useRef<DetectedCard[]>([])
  const lastCountRef = useRef(-1)
  const lastProcessTimeRef = useRef(0)
  /** When true, skip drawing + OpenCV so JPEG encode / takePhoto are not starved on the main thread. */
  const suspendOpenCvRef = useRef(false)

  const [cvReady, setCvReady] = useState(false)
  const [cvLoading, setCvLoading] = useState(false)
  const [cameraActive, setCameraActive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cardData, setCardData] = useState<FullPokemonCard | null>(null)
  const [possibleCards, setPossibleCards] = useState<FullPokemonCard[]>([])
  const [loading, setLoading] = useState(false)
  const [addingToInventory, setAddingToInventory] = useState(false)
  const [addSuccess, setAddSuccess] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)

  const API_URL = getApiUrl()

  useEffect(() => {
    onCameraActiveChange?.(cameraActive)
  }, [cameraActive, onCameraActiveChange])

  const processFrame = useCallback(() => {
    const video = videoRef.current
    const canvas = canvasRef.current
    const overlay = overlayRef.current
    const cv = cvRef.current
    if (!video || !canvas || !overlay || !cv || video.readyState <= 2) return

    const ctx = canvas.getContext('2d')
    const overlayCtx = overlay.getContext('2d')
    if (!ctx || !overlayCtx) return
    if (suspendOpenCvRef.current) return

    const { videoWidth, videoHeight } = video
    canvas.width = videoWidth
    canvas.height = videoHeight
    overlay.width = videoWidth
    overlay.height = videoHeight

    ctx.drawImage(video, 0, 0)
    const imageData = ctx.getImageData(0, 0, videoWidth, videoHeight)

    // Skip detection when frame is mostly black (camera not ready yet)
    const data = imageData.data
    let sum = 0
    for (let i = 0; i < data.length; i += 4) sum += (data[i] + data[i + 1] + data[i + 2]) / 3
    const avgBrightness = sum / (data.length / 4)
    if (avgBrightness < 15) {
      overlayCtx.clearRect(0, 0, videoWidth, videoHeight)
      lastCardsRef.current = []
      return
    }

    // Process at ~8 fps to reduce load (every ~125ms)
    const now = performance.now()
    const shouldProcess = now - lastProcessTimeRef.current >= 125

    let cardsToDraw = lastCardsRef.current
    if (shouldProcess) {
      try {
        const src = (cv as { matFromImageData: (d: ImageData) => { delete: () => void } }).matFromImageData(imageData)
        const frameArea = videoWidth * videoHeight
        const minBboxArea = frameArea * MIN_CARD_AREA_RATIO

        const cvTyped = cv as {
          cvtColor: (a: unknown, b: unknown, c: number) => void
          GaussianBlur: (a: unknown, b: unknown, c: unknown, d: number) => void
          Canny: (a: unknown, b: unknown, c: number, d: number) => void
          MatVector: new () => { size: () => number; get: (i: number) => { delete: () => void }; delete: () => void }
          Mat: new () => { delete: () => void }
          findContours: (a: unknown, b: unknown, c: unknown, d: number, e: number) => void
          contourArea: (c: unknown) => number
          minAreaRect: (c: unknown) => {
            size: { width: number; height: number }
            center: { x: number; y: number }
            angle: number
          }
          inRange: (src: unknown, low: unknown, high: unknown, dst: unknown) => void
          morphologyEx: (src: unknown, dst: unknown, op: number, kernel: unknown) => void
          getStructuringElement: (shape: number, size: unknown) => unknown
          adaptiveThreshold: (src: unknown, dst: unknown, maxVal: number, method: number, type: number, blockSize: number, C: number) => void
          threshold: (src: unknown, dst: unknown, thresh: number, maxVal: number, type: number) => number
          RETR_TREE: number
          CHAIN_APPROX_SIMPLE: number
          COLOR_RGBA2GRAY: number
          COLOR_RGBA2BGR: number
          COLOR_BGR2HSV: number
          MORPH_CLOSE: number
          MORPH_OPEN: number
          MORPH_RECT: number
          Size: new (w: number, h: number) => unknown
          matFromArray: (r: number, c: number, t: number, a: ArrayBufferView) => { delete: () => void }
          CV_8UC1: number
          ADAPTIVE_THRESH_GAUSSIAN_C: number
          THRESH_BINARY: number
          THRESH_OTSU: number
        }

        const morphKernel = (size: number) => {
          const k = size % 2 === 0 ? size + 1 : size
          return cvTyped.getStructuringElement(cvTyped.MORPH_RECT, new cvTyped.Size(k, k))
        }

        const contourToCard = (contour: { delete: () => void }): DetectedCard | null => {
          const rect = cvTyped.minAreaRect(contour)
          let rectW = rect.size.width
          let rectH = rect.size.height
          const smallDim = Math.min(rectW, rectH)
          const largeDim = Math.max(rectW, rectH)
          const rectArea = rectW * rectH

          if (rectArea < minBboxArea || rectArea > frameArea * MAX_CARD_AREA_RATIO) return null
          if (largeDim <= 0) return null

          const aspect = smallDim / largeDim
          const aspectDiff = Math.abs(aspect - CARD_ASPECT_RATIO) / CARD_ASPECT_RATIO
          if (aspectDiff > ASPECT_TOLERANCE) return null

          const confidence = Math.max(0, 1 - aspectDiff / ASPECT_TOLERANCE)
          const cx = rect.center.x
          const cy = rect.center.y
          const angleRad = (rect.angle * Math.PI) / 180
          const cos = Math.cos(angleRad)
          const sin = Math.sin(angleRad)

          const targetSmall = largeDim * CARD_ASPECT_RATIO
          const wasWider = rectW <= rectH
          if (aspect > CARD_ASPECT_RATIO) {
            rectW = wasWider ? targetSmall : largeDim
            rectH = wasWider ? largeDim : targetSmall
          }

          const edgeMargin = Math.min(videoWidth, videoHeight) * 0.04
          if (cx < edgeMargin || cx > videoWidth - edgeMargin || cy < edgeMargin || cy > videoHeight - edgeMargin) return null

          const corners: { x: number; y: number }[] = []
          for (const [dx, dy] of [
            [-rectW / 2, -rectH / 2],
            [rectW / 2, -rectH / 2],
            [rectW / 2, rectH / 2],
            [-rectW / 2, rectH / 2],
          ]) {
            const x = dx * cos - dy * sin + cx
            const y = dx * sin + dy * cos + cy
            if (x < -50 || y < -50 || x > videoWidth + 50 || y > videoHeight + 50) return null
            corners.push({ x, y })
          }
          return { corners, confidence }
        }

        const areaOf = (c: DetectedCard) => {
          const xs = c.corners.map((p) => p.x)
          const ys = c.corners.map((p) => p.y)
          return (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys))
        }

        const extractCardsFromMask = (mask: unknown, minContourArea?: number): DetectedCard[] => {
          const contours = new cvTyped.MatVector()
          const hier = new cvTyped.Mat()
          cvTyped.findContours(mask, contours, hier, cvTyped.RETR_TREE, cvTyped.CHAIN_APPROX_SIMPLE)
          const out: DetectedCard[] = []
          for (let i = 0; i < contours.size(); i++) {
            const c = contours.get(i)
            const area = cvTyped.contourArea(c)
            if (minContourArea != null && area < minContourArea) {
              c.delete()
              continue
            }
            if (area > frameArea * MAX_CARD_AREA_RATIO) {
              c.delete()
              continue
            }
            const card = contourToCard(c)
            if (card) out.push(card)
            c.delete()
          }
          contours.delete()
          hier.delete()
          return out
        }

        /** Prefer outer bounds: keep only cards that are not inside a larger valid card (full card, not inner artwork). */
        const filterPreferOuter = (list: DetectedCard[]): DetectedCard[] => {
          if (list.length <= 1) return list
          const outer: DetectedCard[] = []
          for (const card of list) {
            const cardArea = areaOf(card)
            const acx = card.corners.reduce((s, p) => s + p.x, 0) / 4
            const acy = card.corners.reduce((s, p) => s + p.y, 0) / 4
            let insideLarger = false
            for (const other of list) {
              if (areaOf(other) <= cardArea) continue
              const ocx = Math.min(...other.corners.map((p) => p.x))
              const ocy = Math.min(...other.corners.map((p) => p.y))
              const ocw = Math.max(...other.corners.map((p) => p.x)) - ocx
              const och = Math.max(...other.corners.map((p) => p.y)) - ocy
              if (ocx <= acx && acx <= ocx + ocw && ocy <= acy && acy <= ocy + och) {
                insideLarger = true
                break
              }
            }
            if (!insideLarger) outer.push(card)
          }
          return outer.length > 0 ? outer : list.slice(0, 1)
        }

        const mergeAndPickBest = (allCandidates: DetectedCard[][]): DetectedCard[] => {
          const merged = allCandidates.flat()
          if (merged.length === 0) return []
          const unique = merged.filter((a, i) => {
            for (let j = 0; j < i; j++) {
              const b = merged[j]
              const ax = a.corners.reduce((s, p) => s + p.x, 0) / 4
              const ay = a.corners.reduce((s, p) => s + p.y, 0) / 4
              const bx = b.corners.reduce((s, p) => s + p.x, 0) / 4
              const by = b.corners.reduce((s, p) => s + p.y, 0) / 4
              const dist = Math.hypot(ax - bx, ay - by)
              const minDim = Math.min(
                Math.max(...a.corners.map((p) => p.x)) - Math.min(...a.corners.map((p) => p.x)),
                Math.max(...a.corners.map((p) => p.y)) - Math.min(...a.corners.map((p) => p.y)),
              )
              if (dist < minDim * 0.3) return false
            }
            return true
          })
          const filtered = filterPreferOuter(unique)
          return filtered.sort((a, b) => areaOf(b) - areaOf(a))
        }

        const gray = new cvTyped.Mat()
        cvTyped.cvtColor(src, gray, cvTyped.COLOR_RGBA2GRAY)

        const blurSize = Math.max(3, Math.min(15, Math.floor(videoWidth / 150)))
        const blurK = blurSize % 2 === 0 ? blurSize + 1 : blurSize
        const blurred = new cvTyped.Mat()
        cvTyped.GaussianBlur(gray, blurred, new cvTyped.Size(blurK, blurK), 0)

        const allCandidates: DetectedCard[][] = []

        // Strategy 1: Edge detection – best for cards on table with clear boundaries
        const edges = new cvTyped.Mat()
        cvTyped.Canny(blurred, edges, 25, 80)
        const edgeKernel = morphKernel(Math.max(5, Math.floor(videoWidth / 250)))
        cvTyped.morphologyEx(edges, edges, cvTyped.MORPH_CLOSE, edgeKernel)
        allCandidates.push(extractCardsFromMask(edges, MIN_AREA_PX))
        edges.delete()
        ;(edgeKernel as { delete: () => void }).delete()

        // Strategy 2: Adaptive threshold – card vs table contrast (hand, binder, varying light)
        const adaptive = new cvTyped.Mat()
        const blockSize = Math.max(11, Math.min(41, Math.floor(videoWidth / 40)))
        const block = blockSize % 2 === 0 ? blockSize + 1 : blockSize
        cvTyped.adaptiveThreshold(blurred, adaptive, 255, cvTyped.ADAPTIVE_THRESH_GAUSSIAN_C, cvTyped.THRESH_BINARY, block, 5)
        const adaptiveKernel = morphKernel(5)
        cvTyped.morphologyEx(adaptive, adaptive, cvTyped.MORPH_CLOSE, adaptiveKernel)
        allCandidates.push(extractCardsFromMask(adaptive, MIN_AREA_PX))
        adaptive.delete()
        ;(adaptiveKernel as { delete: () => void }).delete()

        // Strategy 3: Otsu threshold – bimodal scenes (dark background, light card)
        const otsu = new cvTyped.Mat()
        cvTyped.threshold(blurred, otsu, 0, 255, cvTyped.THRESH_BINARY + cvTyped.THRESH_OTSU)
        const otsuKernel = morphKernel(5)
        cvTyped.morphologyEx(otsu, otsu, cvTyped.MORPH_CLOSE, otsuKernel)
        allCandidates.push(extractCardsFromMask(otsu, MIN_AREA_PX))
        otsu.delete()
        ;(otsuKernel as { delete: () => void }).delete()

        gray.delete()
        blurred.delete()

        const result = mergeAndPickBest(allCandidates)
        const prev = lastCardsRef.current
        const maxJump = Math.hypot(videoWidth, videoHeight) * 0.25
        let final = result
        if (result.length > 0 && prev.length > 0) {
          const ncx = result[0].corners.reduce((s, p) => s + p.x, 0) / 4
          const ncy = result[0].corners.reduce((s, p) => s + p.y, 0) / 4
          const pcx = prev[0].corners.reduce((s, p) => s + p.x, 0) / 4
          const pcy = prev[0].corners.reduce((s, p) => s + p.y, 0) / 4
          if (Math.hypot(ncx - pcx, ncy - pcy) > maxJump) final = prev
        }
        cardsToDraw = final
        lastCardsRef.current = final
        lastProcessTimeRef.current = now
        if (final.length !== lastCountRef.current) {
          lastCountRef.current = final.length
        }

        src.delete()
      } catch (err) {
        console.warn('OpenCV processing error:', err)
      }
    }

    overlayCtx.clearRect(0, 0, videoWidth, videoHeight)
    overlayCtx.strokeStyle = '#22c55e'
    overlayCtx.lineWidth = 3
    overlayCtx.font = '14px sans-serif'
    overlayCtx.fillStyle = '#22c55e'

    const card = cardsToDraw[0]
    if (card && card.corners.length >= 4) {
      const { corners } = card
      overlayCtx.beginPath()
      overlayCtx.moveTo(corners[0].x, corners[0].y)
      for (let i = 1; i < corners.length; i++) {
        overlayCtx.lineTo(corners[i].x, corners[i].y)
      }
      overlayCtx.closePath()
      overlayCtx.stroke()
      const cx = corners.reduce((s, p) => s + p.x, 0) / corners.length
      const cy = corners.reduce((s, p) => s + p.y, 0) / corners.length
      overlayCtx.fillText(`Card ${card.confidence.toFixed(2)}`, cx - 30, cy - 5)
    }
  }, [])

  const startCamera = useCallback(async () => {
    setError(null)
    setCvLoading(true)

    try {
      // Yield to let React paint "Loading..." before blocking on OpenCV import
      await new Promise((r) => requestAnimationFrame(r))
      await new Promise((r) => requestAnimationFrame(r))

      const opencvPromise = (async () => {
        if (cvRef.current) return true
        try {
          let cv = (cvModule as { default?: unknown }).default ?? cvModule
          if (cv instanceof Promise) cv = await cv
          const c = cv as { onRuntimeInitialized?: () => void; calledRun?: boolean }
          if (c.onRuntimeInitialized !== undefined && !c.calledRun) {
            await new Promise<void>((r) => {
              c.onRuntimeInitialized = r
            })
          }
          cvRef.current = cv
          return true
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Failed to load OpenCV')
          return false
        }
      })()

      const isPortrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: isPortrait ? 1080 : 1920 },
          height: { ideal: isPortrait ? 1920 : 1080 },
          facingMode: 'environment',
        },
      })

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        streamRef.current = stream

        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play()
          setCameraActive(true)
        }
      }

      const ok = await opencvPromise
      setCvLoading(false)
      if (ok) setCvReady(true)
    } catch (err) {
      setCvLoading(false)
      setError(err instanceof Error ? err.message : 'Failed to access camera')
    }
  }, [])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
    setCameraActive(false)
    onCameraActiveChange?.(false)
    lastCardsRef.current = []
    lastCountRef.current = -1
  }, [onCameraActiveChange])

  useEffect(() => () => stopCamera(), [stopCamera])

  // Process frames when camera is active
  useEffect(() => {
    if (!cameraActive || !cvReady) return

    function tick() {
      processFrame()
      animationRef.current = requestAnimationFrame(tick)
    }
    animationRef.current = requestAnimationFrame(tick)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [cameraActive, cvReady, processFrame])

  const captureAndAnalyze = useCallback(async () => {
    const video = videoRef.current
    const stream = streamRef.current
    if (!video) {
      setError('Camera not available')
      return
    }
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      setError('Please wait for the camera to be ready')
      return
    }

    suspendOpenCvRef.current = true
    await new Promise<void>((r) => requestAnimationFrame(() => r()))

    try {
    const jpegQuality = shouldPreferVideoSnapshot() ? 0.85 : 0.9
    let jpegBlob: Blob | undefined

    const captureFromVideoFrame = async (): Promise<Blob | undefined> => {
      const captureCanvas = document.createElement('canvas')
      const detectedCard = lastCardsRef.current[0]
      if (detectedCard && detectedCard.corners.length >= 4) {
        const xs = detectedCard.corners.map((p) => p.x)
        const ys = detectedCard.corners.map((p) => p.y)
        const x0 = Math.max(0, Math.floor(Math.min(...xs)))
        const y0 = Math.max(0, Math.floor(Math.min(...ys)))
        const x1 = Math.min(video.videoWidth, Math.ceil(Math.max(...xs)))
        const y1 = Math.min(video.videoHeight, Math.ceil(Math.max(...ys)))
        const w = Math.max(1, x1 - x0)
        const h = Math.max(1, y1 - y0)
        captureCanvas.width = w
        captureCanvas.height = h
        const ctx = captureCanvas.getContext('2d')
        if (!ctx) {
          setError('Failed to capture image')
          return undefined
        }
        ctx.drawImage(video, x0, y0, w, h, 0, 0, w, h)
      } else {
        captureCanvas.width = video.videoWidth
        captureCanvas.height = video.videoHeight
        const ctx = captureCanvas.getContext('2d')
        if (!ctx) {
          setError('Failed to capture image')
          return undefined
        }
        ctx.drawImage(video, 0, 0)
      }
      if (captureCanvas.width === 0 || captureCanvas.height === 0) {
        setError('Invalid capture dimensions')
        return undefined
      }
      try {
        return await canvasToJpegBlob(captureCanvas, jpegQuality)
      } catch {
        setError('Failed to encode image')
        return undefined
      }
    }

    const tryImageCapture = async (): Promise<Blob | undefined> => {
      const track = stream?.getVideoTracks()[0]
      const ImageCaptureCtor = (window as Window & { ImageCapture?: new (t: MediaStreamTrack) => { takePhoto: () => Promise<Blob> } }).ImageCapture
      if (!track || !ImageCaptureCtor) return undefined

      const capture = new ImageCaptureCtor(track)
      const blob = await capture.takePhoto()
      const bitmap = await createImageBitmap(blob)
      const detectedCard = lastCardsRef.current[0]

      const captureCanvas = document.createElement('canvas')
      if (detectedCard && detectedCard.corners.length >= 4) {
        const scaleX = bitmap.width / video.videoWidth
        const scaleY = bitmap.height / video.videoHeight
        const xs = detectedCard.corners.map((p) => p.x * scaleX)
        const ys = detectedCard.corners.map((p) => p.y * scaleY)
        const x0 = Math.max(0, Math.floor(Math.min(...xs)))
        const y0 = Math.max(0, Math.floor(Math.min(...ys)))
        const x1 = Math.min(bitmap.width, Math.ceil(Math.max(...xs)))
        const y1 = Math.min(bitmap.height, Math.ceil(Math.max(...ys)))
        const w = Math.max(1, x1 - x0)
        const h = Math.max(1, y1 - y0)
        captureCanvas.width = w
        captureCanvas.height = h
        const ctx = captureCanvas.getContext('2d')
        if (!ctx) {
          bitmap.close()
          throw new Error('Failed to get canvas context')
        }
        ctx.drawImage(bitmap, x0, y0, w, h, 0, 0, w, h)
      } else {
        captureCanvas.width = bitmap.width
        captureCanvas.height = bitmap.height
        const ctx = captureCanvas.getContext('2d')
        if (!ctx) {
          bitmap.close()
          throw new Error('Failed to get canvas context')
        }
        ctx.drawImage(bitmap, 0, 0)
      }
      bitmap.close()
      if (captureCanvas.width === 0 || captureCanvas.height === 0) throw new Error('Invalid dimensions')
      return canvasToJpegBlob(captureCanvas, jpegQuality)
    }

    try {
      if (!shouldPreferVideoSnapshot()) {
        jpegBlob = await tryImageCapture()
      }
    } catch {
      jpegBlob = undefined
    }

    if (!jpegBlob) {
      jpegBlob = await captureFromVideoFrame()
      if (!jpegBlob) return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${API_URL}/api/analyze-card`, {
        method: 'POST',
        credentials: 'include',
        body: jpegBlob,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          (errorData as { error?: string; message?: string }).error ||
            (errorData as { message?: string }).message ||
            `Failed to analyze card (${response.status})`,
        )
      }

      const data = await response.json()
      if (data.bestMatch !== undefined && Array.isArray(data.possibleCards)) {
        setCardData(data.bestMatch)
        setPossibleCards(data.possibleCards)
      } else {
        setCardData(data)
        setPossibleCards([])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred')
    } finally {
      setLoading(false)
    }
    } finally {
      suspendOpenCvRef.current = false
    }
  }, [API_URL])

  const handleAddToInventoryClick = () => {
    if (!cardData) return
    setShowAddModal(true)
  }

  const handleModalConfirm = async (condition: StandardCondition, priceWhenAdded: number | null) => {
    if (!cardData) return
    setAddingToInventory(true)
    setAddSuccess(false)
    setShowAddModal(false)

    try {
      await addCard({
        cardId: cardData.id,
        condition,
        priceWhenAdded: priceWhenAdded ?? undefined,
      })
      setAddSuccess(true)
      setTimeout(() => setAddSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card to inventory')
    } finally {
      setAddingToInventory(false)
    }
  }

  const handleModalClose = () => setShowAddModal(false)

  const handleSelectAlternativeCard = (card: FullPokemonCard) => {
    setCardData(card)
    setAddSuccess(false)
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-4rem)] md:h-auto gap-4 md:gap-6 overflow-hidden">
      {/* Top row: Video (left) | Found card (right) - result hidden on mobile */}
      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:gap-6">
        {/* Video - left, portrait ratio on mobile */}
        <div className="min-h-0 flex flex-col">
          <Card className="h-full shrink-0 border-0 md:border p-0">
            <CardContent className="p-0 h-full">
              <div className="relative w-full h-full min-h-[160px] aspect-3/4 md:aspect-auto md:min-h-[320px] bg-black overflow-hidden">
                <video ref={videoRef} className="absolute inset-0 w-full h-full object-contain" playsInline muted />
                <canvas ref={canvasRef} className="hidden" />
                <canvas ref={overlayRef} className="absolute inset-0 w-full h-full object-contain pointer-events-none" />
                {!cameraActive && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gray-900/80 text-white">
                    {cvLoading ? (
                      <p className="text-lg">Loading OpenCV & starting camera…</p>
                    ) : (
                      <>
                        <p className="text-lg">Camera is off</p>
                        <Button onClick={startCamera} size="lg">
                          Start camera
                        </Button>
                      </>
                    )}
                  </div>
                )}
                {cameraActive && (
                  <>
                    {cvLoading && <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/70 rounded text-sm text-white">Loading detection…</div>}
                    <div className="absolute bottom-4 left-0 right-0 hidden justify-center md:flex">
                      <Button onClick={captureAndAnalyze} disabled={loading} size="sm">
                        {loading ? 'Analyzing...' : 'Capture & analyze'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
          {error && <div className="mt-4 flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-600">{error}</div>}
        </div>

        {/* Found card - right, hidden on mobile */}
        <div className="hidden md:flex shrink-0 w-[280px] lg:w-[320px] flex-col gap-4 overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <p className="text-gray-500 m-0">Analyzing card...</p>
            </div>
          )}

          {cardData && !loading && (
            <Card className="border">
              <CardContent className="p-4 flex flex-col gap-4">
                <div className="aspect-63/88 max-h-[280px] w-full mx-auto bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                  {cardData.cardData.imageUrl ? (
                    <img src={cardData.cardData.imageUrl} alt={cardData.name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="text-gray-400 p-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <p className="font-semibold text-lg m-0 truncate">{cardData.name}</p>
                  {cardData.set && <p className="text-sm text-gray-600 m-0 truncate">{cardData.set.name}</p>}
                  {cardData.collectorNumber && <p className="text-xs text-gray-500 m-0">#{cardData.collectorNumber}</p>}
                  {cardData.cardMarketPrice != null && <p className="text-sm font-medium text-gray-700 m-0">€{cardData.cardMarketPrice.avg.toFixed(2)}</p>}
                </div>
                <Button className="w-full py-4 hidden md:flex" onClick={handleAddToInventoryClick} disabled={addingToInventory}>
                  ➕ Add to Inventory
                </Button>
                {addSuccess && (
                  <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-600 text-sm">
                    Card added to inventory successfully!
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {!cardData && !loading && !error && (
            <div className="text-center py-8 md:py-12">
              <p className="text-gray-600 text-sm m-0">
                Start the camera and point it at a Pokemon card. When a card is detected, click &quot;Capture & analyze&quot; to identify it.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Alternatives - below, smaller */}
      {/* Alternatives - below, smaller. Show skeletons while analyzing. */}
      {(cardData || loading) && (
        <div className="shrink-0 pt-4">
          <ScanCardAlternatives
            possibleCards={possibleCards}
            selectedCard={cardData ?? undefined}
            selectedCardId={cardData?.id}
            onSelectCard={handleSelectAlternativeCard}
            compact
            loading={loading}
          />
        </div>
      )}

      {cameraActive && (
        <div className="fixed bottom-[calc(4rem+max(1.5rem,env(safe-area-inset-bottom)))] right-[max(1.5rem,env(safe-area-inset-right))] flex flex-row gap-3 md:hidden z-50 items-end">
          <Button size="icon" className="h-14 w-14 rounded-full shadow-lg" onClick={captureAndAnalyze} disabled={loading} title={loading ? 'Analyzing...' : 'Capture & analyze'}>
            {loading ? (
              <span className="text-xs">…</span>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                <circle cx="12" cy="13" r="3" />
              </svg>
            )}
          </Button>
          {cardData && (
            <Button
              size="icon"
              className="h-12 w-12 rounded-full shadow-lg bg-green-600 text-white hover:bg-green-700"
              onClick={handleAddToInventoryClick}
              disabled={addingToInventory}
              title="Add to Inventory"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            </Button>
          )}
        </div>
      )}

      <AddToInventoryModal
        key={showAddModal ? (cardData?.id ?? 'no-card') : 'closed'}
        isOpen={showAddModal}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        card={cardData}
      />
    </div>
  )
}
